import Badge from '@/app/components/common/Badge'
import Text from '@/app/components/common/Text'
import Title from '@/app/components/common/Title'
import TranslateString from '@/app/components/common/TranslateString'
import Stars from '@/app/components/Stars'
import { Icons } from '@/app/components/ui/Icons'
import { SearchItem } from '@/app/types'
import { ratingsTranslations } from '@/app/utils'
import { useTranslations } from 'next-intl'
import { usePathname } from 'next/navigation'
import React, { useEffect, useState } from 'react'

interface SiteHeadingProps {
	showNewHeading: boolean
	customHeading: boolean
	selectedItem: SearchItem | null
	sanityData?: {
		headingOne?: string
	}
	isLandingPage?: boolean
	hotel?: any
	/** Server-rendered city name for SEO - prevents flash of default content */
	serverCity?: string
	/** Server-translated heading text - rendered immediately without client translation */
	serverHeading?: string
}

const HeadingLoader = () => <div className="min-h-[60px] w-full"></div>

const SiteHeading: React.FC<SiteHeadingProps> = ({
	showNewHeading,
	customHeading,
	selectedItem,
	sanityData,
	hotel,
	isLandingPage,
	serverCity,
	serverHeading,
}) => {
	const tRating = useTranslations('guestRatings')
	const lang = usePathname().split('/')[1] || 'en'
	const [defaultSelectedItem, setDefaultSelectedItem] =
		useState<SearchItem | null>(null)

	useEffect(() => {
		if (!defaultSelectedItem) {
			setDefaultSelectedItem(selectedItem)
		}
	}, [selectedItem])

	// Determine the city to display - prefer server-rendered city for SEO
	const displayCity =
		selectedItem?.cityonly ||
		serverCity ||
		(isLandingPage ? defaultSelectedItem?.cityonly : '') ||
		''

	const headingContent = () => {
		if (isLandingPage && defaultSelectedItem) {
			return defaultSelectedItem.name
		}

		if (!showNewHeading) {
			return <HeadingLoader />
		}

		// If we have a server-translated heading and no sanity override, use it directly
		// This ensures the H1 is correct on initial render for SEO
		if (serverHeading && !sanityData?.headingOne) {
			return <span dangerouslySetInnerHTML={{ __html: serverHeading }} />
		}

		if (customHeading && !sanityData?.headingOne) {
			// Fallback to client-side translation if serverHeading not provided
			if (displayCity) {
				return (
					<TranslateString
						context="Main"
						i18nKey="destCodePage.siteHeading"
						variables={{
							location: displayCity,
						}}
					/>
				)
			} else {
				return (
					<TranslateString context="Main" i18nKey="newDesign.siteHeading" />
				)
			}
		}

		return sanityData?.headingOne ? (
			<>{sanityData.headingOne}</>
		) : (
			<TranslateString context="Main" i18nKey="newDesign.siteHeading" />
		)
	}

	const Ratings = () => {
		if (hotel?.rating) {
			const mappedRating = tRating(ratingsTranslations(hotel.rating));

			return (
				<Text
					size="md"
					color={'white'}
					className="font-semibold max-lg:text-dark-blue max-xl:text-sm"
				>
					<span className="lg:hidden">{hotel?.rating || '0.0'}</span>{' '}
					{mappedRating}
				</Text>
			)
		}
		return <div className="opacity-30 w-11 h-3 rounded-xl bg-white" />
	}

	if (isLandingPage) {
		return (
			<div className="hotel-site-heading">
				{!hotel ? (
					<Stars
						count={5}
						fill="white"
						className="stars flex items-center opacity-30 justify-center"
					/>
				) : (
					<Stars
						count={hotel?.stars}
						fill="white"
						className="stars flex items-center justify-center"
					/>
				)}
				<Title
					className="site-heading my-2"
					level="1"

					testId="site-heading"
					id="site-heading"
				>
					{headingContent()}
				</Title>
				<div className="hotel-rating full-width max-xl:mb-6">
					<div className="hotel-list-item__location md:min-w-28 md:gap-2 gap-1 flex items-center">
						<Icons.iconPin stroke="currentColor" className="text-black lg:text-white" />
						<div className="hotel-list-item__location-content">
							<Text
								size="md"
								color={'white'}
								className="capitalize max-xl:text-sm"
							>
								{defaultSelectedItem
									? `${defaultSelectedItem?.country}, ${defaultSelectedItem?.cityonly}`
									: ''}
							</Text>
						</div>
					</div>
					<div className="flex row items-center md:min-w-28 gap-2">
						<span className="max-lg:hidden">
							<Badge
								bgColor={hotel?.rating ? 'white' : 'white/30'}
								textColor={hotel?.rating ? 'transparent' : 'white'}
							>
								{hotel?.rating || '0.0'}
							</Badge>
						</span>
						<Ratings />
					</div>
				</div>
			</div>
		)
	}

	return (
		<Title
			className="site-heading leading-[120%]"
			level="1"

			testId="site-heading"
			id="site-heading"
		>
			{headingContent()}
		</Title>
	)
}

export default SiteHeading
